import { useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  Table,
  Typography,
  message,
  Alert,
  Tooltip,
} from "antd";
import {
  UserAddOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { VaultFile, FileAccessGrant, AccessPermission } from "@/types";
import { formatDateOnly, getDaysUntilDeletion } from "@/utils/formatters";
import {
  grantAccess,
  listAccessGrants,
  revokeAccess,
  checkUser,
  inviteGuestUser,
} from "@/services/storageManager";
import useSWR from "swr";

const { Text } = Typography;

interface ShareModalProps {
  file: VaultFile;
  open: boolean;
  onClose: () => void;
}

// Simplified: Only download permission available
const DEFAULT_PERMISSION: AccessPermission = "download";

export default function ShareModal({ file, open, onClose }: ShareModalProps) {
  const [form] = Form.useForm();
  const [isGranting, setIsGranting] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [userCheckStatus, setUserCheckStatus] = useState<{
    checked: boolean;
    exists: boolean;
    email: string;
  } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // Fetch access grants for this file
  const {
    data: grants = [],
    mutate,
    isLoading,
  } = useSWR<FileAccessGrant[]>(
    open ? `/files/${file.id}/access` : null,
    () => listAccessGrants(file.id),
    { revalidateOnFocus: false }
  );

  // Calculate max share duration based on file's auto-deletion date
  const daysUntilFileDeletion = file.scheduledDeletionAt
    ? getDaysUntilDeletion(file.scheduledDeletionAt)
    : 30;

  const handleGrant = async () => {
    try {
      const values = await form.validateFields();
      setIsGranting(true);

      await grantAccess(file.id, {
        grantedTo: values.email,
        permission: DEFAULT_PERMISSION, // Always download permission
        expiresAt: values.expiresAt?.toISOString(),
      });

      message.success(`Access granted to ${values.email}`);
      form.resetFields();
      mutate(); // Refresh the grants list
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to grant access");
    } finally {
      setIsGranting(false);
    }
  };

  const handleRevoke = async (grantId: string, email: string) => {
    try {
      setRevoking(grantId);
      await revokeAccess(file.id, grantId);
      message.success(`Access revoked for ${email}`);
      mutate(); // Refresh the grants list
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to revoke access");
    } finally {
      setRevoking(null);
    }
  };

  const handleCheckUser = async () => {
    try {
      const values = await form.validateFields(["email"]);
      setIsChecking(true);

      const result = await checkUser(values.email);
      setUserCheckStatus({
        checked: true,
        exists: result.exists,
        email: values.email,
      });

      if (result.exists) {
        message.success("User found in Azure AD");
      } else {
        message.warning("User not found. You can invite them as a guest.");
      }
    } catch (error: any) {
      if (error.errorFields) {
        // Validation error - don't show message
        return;
      }
      message.error(error.response?.data?.message || "Failed to check user");
    } finally {
      setIsChecking(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      const values = await form.validateFields(["email"]);
      setIsInviting(true);

      await inviteGuestUser({
        email: values.email,
        sendInvitationMessage: true,
        customMessage: `You have been invited to access a shared file: "${file.fileName}"`,
      });

      message.success(`Invitation sent to ${values.email}`);

      // Update check status to show user now exists
      setUserCheckStatus({
        checked: true,
        exists: true,
        email: values.email,
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
  };

  // Reset user check status when email changes
  const handleEmailChange = () => {
    setUserCheckStatus(null);
  };

  const columns: ColumnsType<FileAccessGrant> = [
    {
      title: "User",
      dataIndex: "grantedTo",
      key: "grantedTo",
      render: (email) => (
        <Space>
          <MailOutlined />
          <Text>{email}</Text>
        </Space>
      ),
    },
    {
      title: "Granted",
      dataIndex: "grantedAt",
      key: "grantedAt",
      width: 120,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 120,
      render: (date) => (date ? formatDateOnly(date) : <Text type="secondary">Never</Text>),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRevoke(record.id, record.grantedTo)}
          loading={revoking === record.id}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Share "${file.fileName}"`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      {/* Auto-deletion warning */}
      <Alert
        type="warning"
        showIcon
        icon={<ClockCircleOutlined />}
        message={`This file will be auto-deleted in ${daysUntilFileDeletion} days`}
        description="Share links cannot extend beyond the file's deletion date."
        style={{ marginBottom: 20 }}
      />

      {/* Grant access form */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          expiresAt: dayjs().add(7, "day"), // Default: 7 days
        }}
      >
        <Form.Item
          name="email"
          label="Share with"
          rules={[
            { required: true, message: "Please enter an email address" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              prefix={<MailOutlined />}
              placeholder="user@example.com"
              size="large"
              onChange={handleEmailChange}
              suffix={
                userCheckStatus?.checked && (
                  <Tooltip
                    title={
                      userCheckStatus.exists
                        ? "User exists in Azure AD"
                        : "User not found in Azure AD"
                    }
                  >
                    {userCheckStatus.exists ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                    )}
                  </Tooltip>
                )
              }
            />
            <Button
              size="large"
              onClick={handleCheckUser}
              loading={isChecking}
            >
              Check
            </Button>
          </Space.Compact>
        </Form.Item>

        {/* Show invite option if user not found */}
        {userCheckStatus?.checked && !userCheckStatus.exists && (
          <Alert
            type="warning"
            showIcon
            message="User not found in Azure AD"
            description={
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>
                  This user is not part of your organization. You can invite them as a guest user
                  to enable secure file sharing.
                </Text>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleInviteUser}
                  loading={isInviting}
                  style={{ marginTop: 8 }}
                >
                  Send guest invitation
                </Button>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="expiresAt"
          label="Expires"
          rules={[
            { required: true, message: "Please select an expiration date" },
          ]}
        >
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            disabledDate={(current) => {
              const tomorrow = dayjs().add(1, "day").startOf("day");
              const maxDate = dayjs().add(daysUntilFileDeletion, "day");
              return current && (current < tomorrow || current > maxDate);
            }}
            format="MMM D, YYYY"
            placeholder="Select expiration date"
          />
        </Form.Item>

        <Tooltip
          title={
            !userCheckStatus?.checked
              ? "Please check if user exists first"
              : !userCheckStatus.exists
              ? "Please invite the user as a guest first"
              : ""
          }
        >
          <Button
            type="primary"
            onClick={handleGrant}
            loading={isGranting}
            icon={<UserAddOutlined />}
            size="large"
            style={{ marginTop: 16 }}
            disabled={!userCheckStatus?.checked || !userCheckStatus.exists}
          >
            Grant access
          </Button>
        </Tooltip>
      </Form>

      {/* Existing grants */}
      {grants.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Users with access ({grants.length})
          </Text>
          <Table
            columns={columns}
            dataSource={grants}
            rowKey="id"
            pagination={false}
            size="small"
            loading={isLoading}
          />
        </div>
      )}

      {grants.length === 0 && !isLoading && (
        <Text type="secondary" style={{ display: "block", marginTop: 24, textAlign: "center" }}>
          No users have access to this file yet
        </Text>
      )}
    </Modal>
  );
}
